import clsx from 'clsx';
import { FC, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import Select from 'react-select';

import { listAllergies, listHealthIssues } from '../../pets';
import { PetModel } from '../../pets/models/PetModel';
import { DraftState } from '../hooks/useDraft';
import {
  QuickSalePlanModel,
  QuickSaleUserModel,
} from '../models/QuickSaleModel';
import { DailyRationField } from './DailyRationField';
import { FieldRow, QuickSaleCard } from './QuickSaleCard';
import { StateBadge } from './StateBadge';

type Option = { value: number; label: string };

type Props = {
  user: QuickSaleUserModel;
  pet: PetModel;
  savedPet: PetModel;
  plan: QuickSalePlanModel | null;
  savedPlan: QuickSalePlanModel | null;
  petsCount: number;
  draftState: DraftState;
};

const MAX_AGE_YEARS = 25;
const MONTHS = Array.from({ length: 12 }, (_, month) => month);

function toOptions(items: Array<{ id: number; name: string }> = []): Option[] {
  return items.map((item) => ({ value: item.id, label: item.name }));
}

const PetCard: FC<Props> = ({
  user,
  pet,
  savedPet,
  plan,
  savedPlan,
  petsCount,
  draftState,
}) => {
  const intl = useIntl();
  const [editing, setEditing] = useState(false);
  const [allergyOptions, setAllergyOptions] = useState<Option[]>([]);
  const [healthIssueOptions, setHealthIssueOptions] = useState<Option[]>([]);
  const { draft, update } = draftState;
  const petDraft = draft.pet || {};
  const petKindId = savedPet.pet_kind?.id;

  useEffect(() => {
    if (!editing || !petKindId) {
      return;
    }
    Promise.all([
      listAllergies({ pet_kind: petKindId }),
      listHealthIssues({ pet_kind: petKindId }),
    ])
      .then(([allergies, healthIssues]) => {
        setAllergyOptions(toOptions(allergies.data));
        setHealthIssueOptions(toOptions(healthIssues.data));
      })
      .catch((error) => console.warn(error));
  }, [editing, petKindId]);

  function setField(field: string, value: unknown, savedValue: unknown) {
    update('pet', { [field]: value === savedValue ? undefined : value });
  }

  function setIds(
    field: 'allergies' | 'health_issues',
    selected: readonly Option[]
  ) {
    const ids = selected.map((option) => option.value).sort();
    const savedIds = savedPet[field].map((item) => item.id).sort();
    update('pet', {
      [field]: ids.join() === savedIds.join() ? undefined : ids,
    });
  }

  function selectedOptions(
    field: 'allergies' | 'health_issues',
    options: Option[]
  ): Option[] {
    const ids = petDraft[field];
    if (!ids) {
      return toOptions(savedPet[field]);
    }
    const known = [...options, ...toOptions(savedPet[field])];
    return ids.map(
      (id) =>
        known.find((option) => option.value === id) || {
          value: id,
          label: String(id),
        }
    );
  }

  const ageYears = petDraft.age_years ?? savedPet.age_years ?? 0;
  const ageMonths = petDraft.age_months ?? savedPet.age_months ?? 0;
  const weight = petDraft.weight_in_kg ?? savedPet.weight_in_kg;
  const gender = petDraft.gender ?? savedPet.gender;
  const name = petDraft.name ?? savedPet.name;
  const isPriority = petDraft.is_priority_pet ?? !!savedPet.is_priority_pet;
  const multiplier =
    petDraft.customer_multiplier ?? savedPet.customer_multiplier ?? 0;

  function renderList(field: 'allergies' | 'health_issues', options: Option[]) {
    const items = selectedOptions(field, options);
    if (editing) {
      return (
        <Select
          isMulti
          options={options}
          value={items}
          onChange={(selected) => setIds(field, selected)}
          placeholder={intl.formatMessage({ id: 'quickSale.none' })}
        />
      );
    }
    return items.length
      ? items.map((item) => item.label).join(', ')
      : intl.formatMessage({ id: 'quickSale.no' });
  }

  return (
    <QuickSaleCard
      icon="fa-dog"
      title={intl.formatMessage({ id: 'quickSale.pet' })}
      editing={editing}
      onToggleEdit={() => setEditing(!editing)}
    >
      <div className="d-flex gap-5 mb-4">
        <div className="symbol symbol-75px flex-shrink-0">
          {savedPet.processed_avatar_url_cache ? (
            <img src={savedPet.processed_avatar_url_cache} alt={name} />
          ) : (
            <div className="symbol-label bg-light">
              <i className="fas fa-paw fs-2x text-gray-400" />
            </div>
          )}
        </div>
        <div className="flex-grow-1 min-w-0">
          {editing ? (
            <input
              className="form-control form-control-sm fw-bolder"
              value={name}
              onChange={(event) =>
                setField('name', event.target.value, savedPet.name)
              }
            />
          ) : (
            <div
              className={clsx(
                'fs-3 fw-bolder',
                petDraft.name !== undefined && 'text-primary'
              )}
            >
              {name}
            </div>
          )}
          <div className="text-gray-600">{savedPet.breed?.name || '-'}</div>
        </div>
      </div>

      <FieldRow
        label={intl.formatMessage({ id: 'quickSale.gender' })}
        changed={petDraft.gender !== undefined}
      >
        <select
          className="form-select form-select-sm"
          value={gender}
          disabled={!editing}
          onChange={(event) =>
            setField('gender', event.target.value, savedPet.gender)
          }
        >
          <option value="male">
            {intl.formatMessage({ id: 'quickSale.male' })}
          </option>
          <option value="female">
            {intl.formatMessage({ id: 'quickSale.female' })}
          </option>
        </select>
      </FieldRow>
      <FieldRow
        label={intl.formatMessage({ id: 'quickSale.age' })}
        changed={
          petDraft.age_years !== undefined || petDraft.age_months !== undefined
        }
      >
        <div className="d-flex gap-2">
          <select
            className="form-select form-select-sm"
            value={ageYears}
            disabled={!editing}
            aria-label={intl.formatMessage({ id: 'quickSale.ageYears' })}
            onChange={(event) =>
              setField(
                'age_years',
                Number(event.target.value),
                savedPet.age_years
              )
            }
          >
            {Array.from({ length: MAX_AGE_YEARS + 1 }, (_, years) => (
              <option key={years} value={years}>
                {intl.formatMessage({ id: 'quickSale.years' }, { years })}
              </option>
            ))}
          </select>
          <select
            className="form-select form-select-sm"
            value={ageMonths}
            disabled={!editing}
            aria-label={intl.formatMessage({ id: 'quickSale.ageMonths' })}
            onChange={(event) =>
              setField(
                'age_months',
                Number(event.target.value),
                savedPet.age_months
              )
            }
          >
            {MONTHS.map((months) => (
              <option key={months} value={months}>
                {intl.formatMessage({ id: 'quickSale.months' }, { months })}
              </option>
            ))}
          </select>
        </div>
      </FieldRow>
      <FieldRow
        label={intl.formatMessage({ id: 'quickSale.weight' })}
        changed={petDraft.weight_in_kg !== undefined}
      >
        <div className="input-group input-group-sm">
          <input
            type="number"
            min="0.1"
            max="150"
            step="0.1"
            className="form-control"
            value={weight ?? ''}
            disabled={!editing}
            onChange={(event) =>
              setField(
                'weight_in_kg',
                event.target.value === ''
                  ? undefined
                  : Number(event.target.value),
                Number(savedPet.weight_in_kg)
              )
            }
          />
          <span className="input-group-text">kg</span>
        </div>
      </FieldRow>
      <FieldRow
        label={intl.formatMessage({ id: 'quickSale.hasPriority' })}
        changed={petDraft.is_priority_pet !== undefined}
      >
        <label className="form-check form-switch form-check-custom form-check-solid justify-content-end">
          <input
            type="checkbox"
            className="form-check-input"
            checked={isPriority}
            // A pet cannot drop the priority: another one has to take it.
            disabled={!!savedPet.is_priority_pet || petsCount <= 1}
            onChange={(event) =>
              setField(
                'is_priority_pet',
                event.target.checked || undefined,
                false
              )
            }
          />
        </label>
      </FieldRow>

      <div className="mt-5">
        <DailyRationField
          grams={plan?.average_grams_per_day ?? null}
          calories={pet.daily_calorie_intake}
          baseGrams={savedPlan?.average_grams_per_day ?? null}
          baseMultiplier={savedPet.customer_multiplier || 0}
          multiplier={multiplier}
          changed={petDraft.customer_multiplier !== undefined}
          onChange={(value) =>
            setField(
              'customer_multiplier',
              value,
              savedPet.customer_multiplier || 0
            )
          }
        />
      </div>

      <div className="row g-3 mb-5">
        <div className="col-6">
          <div className="bg-light rounded p-4 h-100">
            <div className="fw-bolder mb-1">
              {intl.formatMessage({ id: 'quickSale.allergies' })}
            </div>
            <div
              className={clsx(
                'text-gray-700',
                petDraft.allergies && 'text-primary'
              )}
            >
              {renderList('allergies', allergyOptions)}
            </div>
          </div>
        </div>
        <div className="col-6">
          <div className="bg-light rounded p-4 h-100">
            <div className="fw-bolder mb-1">
              {intl.formatMessage({ id: 'quickSale.healthIssues' })}
            </div>
            <div
              className={clsx(
                'text-gray-700',
                petDraft.health_issues && 'text-primary'
              )}
            >
              {renderList('health_issues', healthIssueOptions)}
            </div>
          </div>
        </div>
      </div>

      <div className="d-flex align-items-center justify-content-between bg-light-primary rounded p-4 mb-5">
        <span className="fw-bolder text-primary fs-5">
          {intl.formatMessage({ id: 'quickSale.planState' })}
        </span>
        {plan ? (
          <StateBadge state={plan.state} kind="plan" />
        ) : (
          <span className="text-muted">
            {intl.formatMessage({ id: 'quickSale.noPlan' })}
          </span>
        )}
      </div>

      <div className="d-flex flex-wrap gap-4 text-gray-700">
        <span>
          <i
            className={clsx(
              'fas me-2',
              user.status_chips.is_active_customer
                ? 'fa-check-circle text-success'
                : 'fa-circle text-gray-400'
            )}
          />
          {intl.formatMessage({
            id: user.status_chips.is_active_customer
              ? 'quickSale.activeCustomer'
              : 'quickSale.inactiveCustomer',
          })}
        </span>
        <span>
          <i className="fas fa-paw me-2 text-warning" />
          {intl.formatMessage(
            { id: 'quickSale.petsCount' },
            { count: user.status_chips.pets_count }
          )}
        </span>
        <span>
          <i
            className={clsx(
              'fas fa-exclamation-circle me-2',
              user.status_chips.incidents_count
                ? 'text-danger'
                : 'text-gray-400'
            )}
          />
          {intl.formatMessage(
            { id: 'quickSale.incidents' },
            { count: user.status_chips.incidents_count }
          )}
        </span>
      </div>
    </QuickSaleCard>
  );
};

export { PetCard };
